// ============================================================================
// OnboardingModal.jsx — First-time user walkthrough
// Shows on first visit to Dashboard, dismissible via localStorage flag
// ============================================================================

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const STEPS = [
  {
    emoji: '👋',
    title: 'Welcome to Zenna!',
    body: "I'm Zenna, your ultimate craft companion! I can create patterns and tutorials for 65+ craft types — cross-stitch, Cricut, sewing, quilting, macrame, crochet, knitting, candle making, resin art, and so much more. I even know how to troubleshoot your sewing machine. Let me show you around!",
    cta: "Let's go!",
  },
  {
    emoji: '💬',
    title: 'Just tell me what to make',
    body: "Talk to me in plain English. 'I want to sew a tote bag' or 'make me a butterfly cross-stitch pattern' or 'how do I make a cut and carve candle'. I'll generate designs, write step-by-step instructions, and link you to supplies on Amazon.",
    cta: 'Got it',
  },
  {
    emoji: '✨',
    title: 'Be specific for best results',
    body: "Instead of 'a flower pattern' try 'a cross-stitch pattern of purple lavender sprigs on a white background, small beginner size, 4 colors max'. The magic formula: subject + craft type + colors + size + style.",
    cta: 'Makes sense',
  },
  {
    emoji: '📸',
    title: 'Upload reference photos',
    body: "Have a photo you love? Upload it! Try: 'match the colors of this sunset' or 'make a pattern inspired by this flower'. I can extract color palettes, match DMC threads, and even estimate your body measurements from a full-body photo for custom-fit sewing patterns.",
    cta: 'Cool!',
  },
  {
    emoji: '🎨',
    title: 'Your canvas, your way',
    body: "Every design lands on your canvas where you can edit, add text, rearrange layers, and refine. Need text on a design? Just ask — I place it with real fonts (no AI spelling mistakes!). Everything auto-saves with version history.",
    cta: 'Next',
  },
  {
    emoji: '🔄',
    title: "Don't like something? Just say so",
    body: "Say 'I don't want the pom-poms' or 'make the background darker' and I'll modify the existing design instead of starting over. I can also convert patterns between crafts — ask me to turn a crochet pattern into knitting!",
    cta: 'Next',
  },
  {
    emoji: '📤',
    title: 'Export and print',
    body: "Export as SVG (Cricut/Silhouette), PDF (printable pattern with color keys, stitch charts, and instructions), or PNG. Sewing patterns include sized pattern pieces. Knitting and crochet patterns include validated stitch counts.",
    cta: 'Next',
  },
  {
    emoji: '🛒',
    title: 'Gallery, Marketplace + Challenges',
    body: "Browse the Community Gallery, remix designs, and enter monthly challenges. Sell your patterns in the Marketplace with Stripe payouts. Or just get inspired by what other crafters are making!",
    cta: 'Next',
  },
  {
    emoji: '🔧',
    title: 'Not just patterns — I fix things too',
    body: "Sewing machine acting up? Cricut blade tearing vinyl? Heat press ghosting? Ask me! I can troubleshoot sewing machines, sergers, embroidery machines, cutting machines, heat presses, and more.",
    cta: 'Next',
  },
  {
    emoji: '💜',
    title: 'You get 7 free credits!',
    body: "Each design generation costs 1 credit. Patterns, tutorials, and troubleshooting are free — only image generation uses credits. When you're ready for more, upgrade for monthly credits and commercial use rights. Now go make something beautiful!",
    cta: 'Start creating',
  },
]

export default function OnboardingModal({ onClose }) {
  const [step, setStep] = useState(0)
  const navigate = useNavigate()

  const finish = () => {
    try {
      localStorage.setItem('zenna_onboarding_complete', 'true')
    } catch (e) { /* ignore */ }
    onClose()
  }

  const next = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1)
    } else {
      finish()
      navigate('/workspace')
    }
  }

  const current = STEPS[step]
  const progress = ((step + 1) / STEPS.length) * 100

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-200 dark:border-slate-700">
        {/* Progress bar */}
        <div className="h-1 bg-slate-200 dark:bg-slate-700">
          <div
            className="h-full bg-gradient-to-r from-zenna-violet to-zenna-cyan transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Close */}
        <div className="flex justify-end p-3">
          <button
            onClick={finish}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-sm"
          >
            Skip tour ✕
          </button>
        </div>

        {/* Content */}
        <div className="px-8 pb-8 text-center">
          <div className="text-6xl mb-4">{current.emoji}</div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
            {current.title}
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-6 whitespace-pre-line">
            {current.body}
          </p>

          {/* Step dots */}
          <div className="flex items-center justify-center gap-1.5 mb-6">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === step
                    ? 'w-6 bg-zenna-violet'
                    : i < step
                    ? 'w-1.5 bg-zenna-violet/50'
                    : 'w-1.5 bg-slate-300 dark:bg-slate-600'
                }`}
                aria-label={`Go to step ${i + 1}`}
              />
            ))}
          </div>

          <div className="flex gap-3">
            {step > 0 && (
              <button
                onClick={() => setStep(step - 1)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition"
              >
                Back
              </button>
            )}
            <button
              onClick={next}
              className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-zenna-violet to-zenna-cyan text-white text-sm font-medium hover:opacity-90 transition"
            >
              {current.cta}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
